import { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { LWApiBaseEventsI } from '@liquidweb-dev/club-sauce/api';
import api from 'modules/queries/api';
import { useOidcSilent } from './silentProvider';
import useAuthenticated from './useAuthenticated';
import useLogin from './useLogin';

const REFRESH_DEBOUNCE_MS = 60_000; // 1 minute
const HTTP_4XX_STATUSES = [401, 403];
const route4xxExceptions = [
	// twofactor/enable and disable returns 401 when user enter wrong passcode
	'account/auth/twofactor/enable.json',
	'account/auth/twofactor/disable.json',
];

export function ApiUnauthorizedConnector() {
	const { activeNavigator } = useAuth();
	const isAuthenticated = useAuthenticated();
	const [, silentRefresh] = useOidcSilent();
	const login = useLogin();

	const isReadyRef = useRef(!activeNavigator);
	isReadyRef.current = !activeNavigator;

	// Handle 4xx statuses
	useEffect(() => {
		const controller = new AbortController();
		let lastPing = 0;
		let lastRefresh = 0;

		const handleError: LWApiBaseEventsI['error'] = (event) => {
			const { uri, status } = event;

			// Avoid doubling up on other work
			if (!isReadyRef.current) return;

			// Just handle the 4xx statuses we want
			if (!HTTP_4XX_STATUSES.includes(status)) return;

			// We don't need to refresh the token for some uris
			if (route4xxExceptions.includes(uri)) return;

			if (status !== 401) {
				// Do nothing, we intended to be not authed
				if (!isAuthenticated) return;

				// Prevent constant pinging
				if (Date.now() > lastPing + REFRESH_DEBOUNCE_MS) return;
				lastPing = Date.now();

				// Trigger a 401 if we have bad auth
				api.utilities.info.apiRaw.ping().catch(() => 0);
				return;
			}

			// Prevent constant loading state
			if (Date.now() > lastRefresh + REFRESH_DEBOUNCE_MS) return;
			lastRefresh = Date.now();

			silentRefresh({ signal: controller.signal })
				.catch(() => null)
				.then((user) => {
					if (controller.signal.aborted) return;

					// Do nothing if we intended to be not authed
					if (!isAuthenticated) return;
					// Check valid new user
					if (user?.expires_at && user?.expires_at > Date.now() / 1000) return;

					// Start over since we're invalid now
					login();
				});
		};

		api.on('error', handleError);

		return () => {
			controller.abort();
			api.off('error', handleError);
		};
	}, [isAuthenticated, login, silentRefresh]);

	return null;
}

export default ApiUnauthorizedConnector;
